import {
  map,
  Observable,
  startWith,
  Subject,
  switchMap,
  take,
  timer
} from 'rxjs';
import dayjs from 'dayjs';
import plugin from 'dayjs/plugin/duration';

import { isString } from './common';

export type Countdown = {
  secondsLeft: number;
  duration: plugin.Duration|null;
  progress: number|null;
};

export type CountdownOptions = {
  timeShift: number;
  tickInterval: number;
  secondsTotal: number|null;
  maxDiffToSync: number;
  skipDuration: boolean;
};

const defaultOptions: CountdownOptions = {
  timeShift: 0,
  tickInterval: 1000,
  secondsTotal: null,
  maxDiffToSync: 1,
  skipDuration: false
};

function calcMaxTicks(secondsLeft: number, tickInterval: number): number {
  const maxTicks = Math.round(secondsLeft * 1000 / tickInterval + 1);

  return Math.max(maxTicks, 1); // we need minimum of 1 tick
}

function calcSecondsLeft(endAt: string|number, timeShift: number): number {
  const endAtMs: number = isString(endAt) ? Number(new Date(endAt)) : endAt;

  return (endAtMs - Date.now() + timeShift) / 1000;
}

function now(): number {
  return Math.floor(Date.now() / 1000);
}

export function addLeadingZero(value: number|string, split: true): [string, string]
export function addLeadingZero(value: number|string, split: false): string
export function addLeadingZero(value: number|string, split = false): string|[string, string] {
  const result: string = String(value).padStart(2, '0');

  if(split) {
    return result.split('') as [string, string];
  }

  return result;
}

/**
 * @param endAt UTC string or timestamp
 * @param userOptions Custom options
 * @return Observable<Countdown>
 */
export function countdown(endAt: string|number, userOptions: Partial<CountdownOptions> = {}): Observable<Countdown> {
  const options: CountdownOptions = {
    ...defaultOptions,
    ...userOptions
  };
  
  const cd = new Observable<Countdown>(subscriber => {
    let secondsLeft: number;
    let predictedEndAt: number;
    let maxTicks: number;

    const reset$ = new Subject<void>();

    const timerSubscription = reset$
      .pipe(
        startWith(0),
        switchMap(() => {
          secondsLeft = calcSecondsLeft(endAt, options.timeShift);
          predictedEndAt = now() + secondsLeft;
          maxTicks = calcMaxTicks(secondsLeft, options.tickInterval);

          return timer(0, options.tickInterval)
            .pipe( take(maxTicks) );
        })
      )
      .subscribe((tick: number) => {
        const secondsLeftNow: number = Math.max(((secondsLeft * 1000) - (tick * options.tickInterval)) / 1000, 0);

        if((tick + 1) === maxTicks) {
          subscriber.next({
            secondsLeft: 0,
            duration: options.skipDuration ? null : dayjs.duration(0, 'seconds'),
            progress: options.secondsTotal ? 100 : null
          });

          subscriber.complete();

          return;
        }

        // sync timer every 5 ticks (5 seconds, 500ms etc)
        if(tick % 5 === 0) {
          const secondsMustBeNow = Math.max(predictedEndAt - now(), 0);

          if(Math.abs(secondsMustBeNow - secondsLeftNow) > options.maxDiffToSync) {
            return reset$.next();
          }
        }

        let progress: number|null = null;

        if(options.secondsTotal && options.secondsTotal > 0) {
          progress = secondsLeftNow / options.secondsTotal * 100;
        }

        subscriber.next({
          secondsLeft: secondsLeftNow,
          duration: options.skipDuration ? null : dayjs.duration(secondsLeftNow, 'seconds'),
          progress
        });
      });

    subscriber.add(() => timerSubscription.unsubscribe());
  });

  return cd.pipe(
    startWith({
      secondsLeft: 0,
      duration: options.skipDuration ? null : dayjs.duration(0, 'seconds'),
      progress: null
    })
  );
}

export function transformToCountdown(endAt: string|null|undefined, format = 'HH:mm:ss'): Observable<string>|undefined {
  if(!endAt) {
    return undefined;
  }

  return countdown(endAt).pipe(
    map((cd: Countdown) => cd.duration!.format(format))
  );
}